import { createAction, createSelector, createSlice } from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import findIndex from 'lodash/findIndex';
import { ISSUE_NOTIFICATION_RECEIVED } from '../socket/constants';

export const fetchApptService = createAction('apptService/fetchApptService');
export const postHistoryFlow = createAction('apptService/postHistoryFlow');
export const putHistoryFlow = createAction('apptService/putHistoryFlow');
export const putHistoryFileUrls = createAction('apptService/putHistoryFileUrls');
export const putHistoryNotes = createAction('apptService/putHistoryNotes');
export const postMrrFlow = createAction('apptService/postMrrFlow');
export const putMrrFlow = createAction('apptService/putMrrFlow');
export const putMrrFileUrls = createAction('apptService/putMrrFileUrls');
export const putMrrNotes = createAction('apptService/putMrrNotes');
export const postScribeFlow = createAction('apptService/postScribeFlow');
export const putScribeFlow = createAction('apptService/putScribeFlow');
export const putScribeFileUrls = createAction('apptService/putScribeFileUrls');
export const putScribeNotes = createAction('apptService/putScribeNotes');
export const postRfaFlow = createAction('apptService/postRfaFlow');
export const putRfaFlow = createAction('apptService/putRfaFlow');
export const putRfaFileUrls = createAction('apptService/putRfaFileUrls');
export const putRfaNotes = createAction('apptService/putRfaNotes');
export const putInsuranceDetails = createAction('apptService/putInsuranceDetails');
export const fetchAppointmentsByDate = createAction('apptService/fetchAppointmentsByDate');
export const fetchAppointmentsByPatient = createAction('apptService/fetchAppointmentsByPatient');
export const fetchFlowSummariesByDate = createAction('apptService/fetchFlowSummariesByDate');
export const fetchMrrByAppointmentId = createAction('apptService/fetchMrrByAppointmentId');
export const fetchPatientDetails = createAction('apptService/fetchPatientDetails');
export const fetchNotifications = createAction('apptService/fetchNotifications');
export const markNotificationSeen = createAction('apptService/markNotificationSeen');

export const notificationsSelector = createSelector(
  (state) => state.apptService.notifications,
  (subState) => subState,
);
export const pwsHistoryFlowSelector = createSelector(
  (state) => state.apptService.pwsHistoryFlow,
  (subState) => subState,
);
export const pwsMrrFlowSelector = createSelector(
  (state) => state.apptService.pwsMrrFlow,
  (subState) => subState,
);
export const pwsRFAFlowSelector = createSelector(
  (state) => state.apptService.pwsRFAFlow,
  (subState) => subState,
);
export const pwsScribeFlowSelector = createSelector(
  (state) => state.apptService.pwsScribeFlow,
  (subState) => subState,
);
export const patientDetailsSelector = createSelector(
  (state) => state.apptService.patientDetails,
  (subState) => subState,
);
export const providerDetailsSelector = createSelector(
  (state) => state.apptService.providerDetails,
  (subState) => subState,
);
export const insuranceDetailsSelector = createSelector(
  (state) => state.apptService.insuranceDetails,
  (subState) => subState,
);
export const serviceDataSelector = createSelector(
  (state) => state.apptService.serviceData,
  (subState) => subState,
);

export const showHistorySelector = createSelector(
  (state) => state.apptService.showHistory,
  (subState) => subState,
);
export const showMrrSelector = createSelector(
  (state) => state.apptService.showMrr,
  (subState) => subState,
);
export const showRFASelector = createSelector(
  (state) => state.apptService.showRFA,
  (subState) => subState,
);
export const showScribeSelector = createSelector(
  (state) => state.apptService.showScribe,
  (subState) => subState,
);

export const appointmentsSelector = createSelector(
  (state) => state.apptService.appointments,
  (subState) => subState,
);
export const flowSummariesSelector = createSelector(
  (state) => state.apptService.flowSummaries,
  (subState) => subState,
);

export const serviceTabSelector = createSelector(
  (state) => state.apptService.activePtServiceTab,
  (subState) => subState,
);

const apptService = createSlice({
  name: 'apptService',
  initialState: {
    pwsHistoryFlow: {},
    pwsMrrFlow: {},
    pwsRFAFlow: {},
    pwsScribeFlow: {},
    patientDetails: {},
    providerDetails: {},
    insuranceDetails: {},
    serviceData: {},
    showHistory: false,
    showMrr: false,
    showRFA: false,
    showScribe: false,
    appointments: [],
    flowSummaries: {},
    activePtServiceTab: 'Intake',
    notifications: [],
  },
  reducers: {
    fetchMrrByAppointmentIdSuccess(state, action) {
      const { data } = action.payload;
      state.pwsMrrFlow = data || {};
    },
    fetchAppointmentsByDateSuccess(state, action) {
      const { data } = action.payload;
      state.appointments = data || [];
    },
    fetchAppointmentsByPatientSuccess(state, action) {
      const { data = [] } = action.payload;
      state.appointments = data;
    },
    appendVisitInAppointment(state, action) {
      const { data } = action.payload;
      state.appointments = [...state.appointments, data];
    },
    fetchFlowSummariesByDateSuccess(state, action) {
      const { data } = action.payload;
      state.flowSummaries = data || [];
    },
    fetchApptServiceSuccess(state, action) {
      const { data } = action.payload;
      const { patientDetails = {}, providerDetails = {}, pwsHistoryFlow = {}, pwsMrrFlow = {}, pwsRFAFlow = {}, pwsScribeFlow = {}, insuranceDetails = {} } = data;
      state.patientDetails = patientDetails;
      state.providerDetails = providerDetails;
      state.pwsHistoryFlow = pwsHistoryFlow;
      state.pwsMrrFlow = pwsMrrFlow;
      state.pwsScribeFlow = pwsScribeFlow;
      state.pwsRFAFlow = pwsRFAFlow;
      state.activePtServiceTab = 'Intake';
      state.insuranceDetails = insuranceDetails || {};
      state.serviceData = data;
    },
    rescheduleAppointmentSuccess(state, action) {
      const { data = {} } = action.payload;
      const { dueAt, date } = data;
      // TODO: Appointment Refactor when normalized
      state.appointments = state.appointments.map((a) => {
        if (a.id === data.id) {
          return { ...a, dueAt, date };
        }
        return a;
      });
    },
    postHistoryFlowSuccess(state, action) {
      const { data } = action.payload;
      const { insuranceDetails = {}, patientDetails = {} } = data;
      state.patientDetails = { ...state.patientDetails, ...patientDetails };
      state.insuranceDetails = insuranceDetails || {};
      state.pwsHistoryFlow = omit(data, ['insuranceDetails', 'patientDetails']);
    },
    postMrrFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsMrrFlow = data;
    },
    postScribeFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsScribeFlow = data;
    },
    postRfaFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsRFAFlow = data;
    },
    putHistoryFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsHistoryFlow = data;
    },
    putMrrFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsMrrFlow = data;
    },
    onChangeMRRFlowSummary(state, action) {
      const {
        payload: { summaryId, value },
      } = action;
      const index = findIndex(state.pwsMrrFlow.summaries, (o) => o.id === summaryId);
      state.pwsMrrFlow.summaries[index].summary = value;
    },
    putScribeFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsScribeFlow = data;
    },
    putRfaFlowSuccess(state, action) {
      const { data } = action.payload;
      state.pwsRFAFlow = data;
    },
    putInsuranceDetailsSuccess(state, action) {
      const { data } = action.payload;
      state.insuranceDetails = data;
    },
    toggleHistory(state, action) {
      state.showHistory = !state.showHistory;
    },
    toggleMrr(state, action) {
      state.showMrr = !state.showMrr;
    },
    toggleRFA(state, action) {
      state.showRFA = !state.showRFA;
    },
    toggleScribe(state, action) {
      state.showScribe = !state.showScribe;
    },
    onChangePtServiceTab(state, action) {
      const { name } = action.payload;
      state.activePtServiceTab = name;
    },
    putPatientDetailsSuccess(state, action) {
      const { data } = action.payload;
      state.patientDetails = data;
    },
    resetApptServiceData(state) {
      state.pwsHistoryFlow = {};
      state.pwsMrrFlow = {};
      state.pwsRFAFlow = {};
      state.pwsScribeFlow = {};
      state.patientDetails = {};
      state.providerDetails = {};
      state.insuranceDetails = {};
      state.serviceData = {};
      state.showHistory = false;
      state.showMrr = false;
      state.showRFA = false;
      state.showScribe = false;
      state.appointments = [];
      state.flowSummaries = {};
      state.activePtServiceTab = 'Intake ';
    },
    fetchNotificationsSuccess(state, action) {
      const { data } = action.payload;
      state.notifications = data;
    },
    markNotificationSeenSuccess(state, action) {
      const {
        data: { id },
      } = action.payload;
      state.notifications = state.notifications.filter((a) => a.id !== id);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ISSUE_NOTIFICATION_RECEIVED, (state, action) => {
      state.notifications.unshift(action.payload);
    });
  },
});

const { actions, reducer } = apptService;
export const {
  fetchMrrByAppointmentIdSuccess,
  fetchAppointmentsByDateSuccess,
  fetchAppointmentsByPatientSuccess,
  fetchFlowSummariesByDateSuccess,
  fetchApptServiceSuccess,
  postHistoryFlowSuccess,
  putHistoryFlowSuccess,
  postMrrFlowSuccess,
  putMrrFlowSuccess,
  postScribeFlowSuccess,
  putScribeFlowSuccess,
  postRfaFlowSuccess,
  putRfaFlowSuccess,
  putInsuranceDetailsSuccess,
  toggleHistory,
  toggleMrr,
  toggleRFA,
  toggleScribe,
  onChangePtServiceTab,
  putPatientDetailsSuccess,
  resetApptServiceData,
  rescheduleAppointmentSuccess,
  appendVisitInAppointment,
  onChangeMRRFlowSummary,
  fetchNotificationsSuccess,
  markNotificationSeenSuccess,
} = actions;

export default reducer;
